import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { saveAs } from 'file-saver';
import { Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination, TextField, Card, CardContent, Typography } from '@mui/material';

function MyDailySales() {
  const [salesData, setSalesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [username, setUsername] = useState('');
  const [totalSales, setTotalSales] = useState(0);
  const [totalCardSales, setTotalCardSales] = useState(0);
  const [totalCashSales, setTotalCashSales] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    // Set today's date by default for start and end date
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);

    // Fetch today's data initially
    fetchData(today, today);
  }, []);

  const fetchData = async (fromDate, toDate) => {
    const user = JSON.parse(localStorage.getItem("emilia"))?.username;
    setUsername(user);  // Set logged-in username
    try {
      const response = await axios.post('https://serveremilia.mylksystems.com/sales/getMyDailySalesforPeriod', {
        username: user,
        from_Date: fromDate,
        to_Date: toDate
      });
      setSalesData(response.data);
      setFilteredData(response.data); // Set the fetched data to be displayed
      calculateTotals(response.data); // Calculate totals for the fetched data
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleFilter = () => {
    fetchData(startDate, endDate); // Fetch filtered data based on selected date range
  };

  const calculateTotals = (data) => {
    const cardTotal = data.reduce((acc, curr) => acc + parseFloat(curr.card_total || 0), 0);
    const cashTotal = data.reduce((acc, curr) => acc + parseFloat(curr.cash_total || 0), 0);
    const allTotal = data.reduce((acc, curr) => acc + parseFloat(curr.all_total || 0), 0);

    setTotalSales(allTotal);
    setTotalCardSales(cardTotal);
    setTotalCashSales(cashTotal);
  };

  const handleDownloadCSV = () => {
    const csvData = [
      ['Inv_date', 'Username', 'Card Total', 'Cash Total', 'All Total'],
      ...filteredData.map(item => [
        item.inv_date,
        item.username,
        item.card_total,
        item.cash_total,
        item.all_total
      ])
    ];

    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'daily_sales.csv');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <div>
      <Typography variant="h6" style={{ marginBottom: '20px' }}>
        Sales report for {username} (Logged-in User)
      </Typography>
      
      <div style={{ marginBottom: '20px' }}>
        <TextField
          label="From Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          style={{ marginRight: '10px' }}
        />
        <TextField
          label="To Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          style={{ marginRight: '10px' }}
        />
        <Button variant="contained" onClick={handleFilter}>Search</Button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '20px' }}>
        <Card style={{ width: '30%' }}>
          <CardContent>
            <Typography variant="h6">Total Sales</Typography>
            <Typography variant="h5">{totalSales.toFixed(2)}</Typography>
          </CardContent>
        </Card>
        <Card style={{ width: '30%' }}>
          <CardContent>
            <Typography variant="h6">Total Cash Sales</Typography>
            <Typography variant="h5">{totalCashSales.toFixed(2)}</Typography>
          </CardContent>
        </Card>
        <Card style={{ width: '30%' }}>
          <CardContent>
            <Typography variant="h6">Total Card Sales</Typography>
            <Typography variant="h5">{totalCardSales.toFixed(2)}</Typography>
          </CardContent>
        </Card>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={filteredData}>
          <XAxis dataKey="inv_date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="card_total" fill="#007bff" />
          <Bar dataKey="cash_total" fill="#f39c12" />
        </BarChart>
      </ResponsiveContainer>

      <div style={{ margin: '20px 0' }}>
        <Button variant="contained" color="primary" onClick={handleDownloadCSV}>
          Download CSV
        </Button>
      </div>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Inv Date</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Card Total</TableCell>
              <TableCell>Cash Total</TableCell>
              <TableCell>All Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
              <TableRow key={row.inv_date}>
                <TableCell>{row.inv_date}</TableCell>
                <TableCell>{row.username}</TableCell>
                <TableCell>{row.card_total}</TableCell>
                <TableCell>{row.cash_total}</TableCell>
                <TableCell>{row.all_total}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </div>
  );
}

export default MyDailySales;
